/**
 * The phone frame box, transcribed from `Oprappo P0 Screens.dc.html`.
 *
 * Every phone frame in the file is the same box:
 *
 *     <div style="background:#F4F0E7;border:1px solid #E2DED5;height:680px;
 *                 display:flex;flex-direction:column;box-sizing:border-box">
 *
 * with the wrapper above it carrying `width:360px`. The visual-regression harness
 * screenshots THAT element (tools/visual-regression/lib/frames.mjs), so the border is
 * inside the captured 360×680 box and is transcribed rather than treated as canvas
 * chrome. Flagged in the WP-C report: B4 paints its border the same colour as its own
 * ground, which reads like artboard chrome rather than UI — a design question, not one
 * resolved here.
 *
 * ── THE GEOMETRY IS NO LONGER DECLARED HERE ─────────────────────────────────────
 * `w-[360px] h-[680px]` used to be written on the next line, and five other frame
 * components each wrote their own copy of it. Those are the numbers the DESIGN DOCUMENT
 * draws a phone at — an artboard — and shipping them as the app's layout is why the app
 * was a 360px column on a laptop and a cut-off screen on a phone.
 *
 * It now comes from `responsive/frame.ts`, which explains the whole defect and holds the
 * one definition. At 360×680 it computes to exactly what was here, which is how the 2%
 * visual gate stays meaningful across the change rather than being relaxed for it.
 *
 * Padding and background differ per frame, so both are props: this component owns only
 * the geometry every frame shares.
 */
import type { ReactNode } from 'react';
import { FRAME_BOX } from '../../responsive/frame.js';

export interface FrameProps {
  children: ReactNode;
  /** Tailwind classes for this frame's own background, border, padding and overflow. */
  className?: string | undefined;
}

export function Frame({ children, className }: FrameProps): JSX.Element {
  return (
    <div className={FRAME_BOX + (className === undefined ? '' : ' ' + className)}>{children}</div>
  );
}

/** `background:#F4F0E7;border:1px solid #E2DED5` — the default for every light frame. */
export const SAND_FRAME = 'bg-sand border border-border';

/**
 * A1's radio: `width:20px;height:20px;border:2px solid …;border-radius:50%` with a 10px
 * teal dot. Moved here from `language.tsx` when A3 and A7 gained the channel choice
 * (W2-12), which reuses A1's row vocabulary rather than inventing a second one.
 */
export function Radio({ selected }: { selected: boolean }): JSX.Element {
  return (
    <div
      className={
        'w-[20px] h-[20px] border-2 rounded-full flex items-center justify-center flex-none ' +
        (selected ? 'border-teal' : 'border-border')
      }
    >
      {selected ? <div className="w-[10px] h-[10px] bg-teal rounded-full" /> : null}
    </div>
  );
}

/**
 * The field label every onboarding form draws: `500 14px/1.75` in ink2, 7px above.
 * A3's and A7's `মোবাইল নম্বর` are transcribed with it; the recovery form reuses it.
 */
export const FIELD_LABEL = 'block font-anek font-medium text-[14px] leading-[1.75] text-ink2 mb-[7px]';

/**
 * § 02's error field: `border:1.5px #B3271E; background:#FAF2F1`
 * (`packages/ui/src/Input.tsx § error`). Swapped in for a field's own border when the
 * field is what is wrong.
 */
export const FIELD_ERROR_BOX = 'border-[1.5px] border-danger bg-danger-surface';

/** § 02's error line under a field: `500 13px/1.75` in `#8E2018`. */
export const FIELD_ERROR_LINE = 'font-anek font-medium text-[13px] leading-[1.75] text-danger-ink';

/**
 * The Oprappo mark, transcribed from A1 (44px) and A7 (40px).
 * Two strokes, teal then amber, `stroke-width:12`, round caps and joins.
 */
export function Mark({ size }: { size: number }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" aria-label="Oprappo mark">
      <path
        d="M24 76 L24 40 Q24 24 40 24 L50 24"
        stroke="#0E3A45"
        strokeWidth="12"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M76 24 L76 60 Q76 76 60 76 L50 76"
        stroke="#E8A33D"
        strokeWidth="12"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/*
 * The P0 back arrow (`M15 5l-7 7 7 7`) and row chevron (`M9 6l6 6-6 6`) used to live
 * here. They are neither of them the `@oprappo/ui` glyph of the same name, so they moved
 * to `./glyphs.js` alongside the other twelve paths the § 01 icon set does not carry —
 * one place to look, and one list in the report.
 *
 * `IconButton control="back"` is still used wherever the control needs its fixed Bangla
 * accessible name; `Glyph name="backArrow"` is used where the frame's geometry is being
 * matched and the arrow is decoration inside a named control.
 */
